<template>
  <div class="form__element form__element--floating">
    <div class="form__input form__input--negative form__input--with-select">
      <div class="form__select">
        <input type="hidden" ref="hidden" :name="id" :id="'hidden-input-' + id" value="">
        <div :aria-controls="'options-' + id" aria-expanded="false" aria-haspopup="listbox" aria-label="Country code" :id="id" class="form__select-field form__select-field--border-transparent form__select-field--no-bg form__select-field--no-padding" role="combobox" tabindex="0">
          <div class="form__select-field-icon" data-icon-path="/images/icons/svg/">
            <span class="icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                  <path d="M0.497656 10.7826C1.628 13.829 4.56034 16 8.00006 16C11.4398 16 14.3721 13.829 15.5025 10.7826L8.00006 10.087L0.497656 10.7826Z" fill="#FFDA44"></path>
                  <path d="M8.00006 0C4.56034 0 1.628 2.171 0.497656 5.21741L8.00006 5.91303L15.5025 5.21738C14.3721 2.171 11.4398 0 8.00006 0Z" fill="black"></path>
                  <path d="M0.497656 5.21741C0.176094 6.08406 0 7.02148 0 8.00004C0 8.97861 0.176094 9.91595 0.497656 10.7826H15.5025C15.824 9.91595 16 8.97861 16 8.00004C16 7.02148 15.8241 6.08403 15.5025 5.21738L0.497656 5.21741Z" fill="#D80027"></path>
              </svg>
            </span>
          </div>
          <div class="form__select-field-label">
            <span class="label"></span>
          </div>
          <div class="form__select-field-action-icon form__select-field-action-icon--open">
            <span class="icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" aria-hidden="true" focusable="false" role="presentation">
                <path fill-rule="evenodd" d="M24.528 11.436c.627.585.63 1.534.007 2.122l-9.546 9c-.3.283-.71.442-1.135.442-.426 0-.834-.16-1.135-.442L9.465 19.49a1.435 1.435 0 0 1 .007-2.121 1.676 1.676 0 0 1 2.263.006l2.119 1.998 8.411-7.93a1.676 1.676 0 0 1 2.263-.007Z" clip-rule="evenodd"></path>
              </svg>
            </span>
          </div>
          <div class="form__select-field-action-icon form__select-field-action-icon--closed">
            <span class="icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" aria-hidden="true" focusable="false" role="presentation">
                <path d="m16.507 18.68 6.508-6.18 1.485 1.41-7.993 7.59L8.5 13.91 10 12.5l6.507 6.18Z"></path>
              </svg>
            </span>
          </div>
        </div>
        <div class="form__select-options" role="listbox" :id="'options-' + id" aria-labelledby="c4aaoX" tabindex="-1">
          <div role="option"
               v-for="(item, index) in cc"
               :id="'option-' + id + '-' + index"
               class="form__select-option"
               @click="handleClick"
               :aria-selected="item === selectedOption"
               :data-value="item.country"
               :data-icon-name="'flag_' + item.country">
            <div class="form__select-option-icon">
              <span class="icon">
                <flagDe v-if="item.country === 'de'"/>
                <flagAt v-if="item.country === 'at'"/>
                <flagCh v-if="item.country === 'ch'"/>
                <flagLu v-if="item.country === 'lu'"/>
                <flagFr v-if="item.country === 'fr'"/>
                <flagBe v-if="item.country === 'be'"/>
                <flagNl v-if="item.country === 'nl'"/>
              </span>
            </div>
            <div class="form__select-option-label">
              <span class="label">{{ t('et.phone.cc.' + item.country ) }} +{{ item.code }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="form__input-main-wrapper">
        <input class="form__input-field"
               ref="input"
               :autocomplete="autocomplete"
               type="tel"
               :id="id + '-' + inputId"
               :aria-invalid="hasError ? 'true' : null"
               :aria-describedby="hasError ? `error-${id}-${inputId}` : null"
               @input="handleInput"
               @blur="handleBlur"
               :value="modelValue"
               :placeholder="label">
        <span class="form__label">
          <label class="label" :for="id + '-' + inputId">{{ label }}</label>
        </span>
      </div>
    </div>
    <Error v-if="hasError"
           :inverted="inverted"
           :id="'error-' + id + '-' + inputId"
           :message="errorMessage" />
  </div>
</template>

<script setup>
import {ref, computed, watch, useId, useTemplateRef, onMounted} from 'vue';
import Error from '../fields/Error.vue';
import {useI18n} from 'vue-i18n';
const { t } = useI18n();

import flagDe from '../../../../../../public/images/icons/svg/flag_de.svg?component';
import flagAt from '../../../../../../public/images/icons/svg/flag_at.svg?component';
import flagCh from '../../../../../../public/images/icons/svg/flag_ch.svg?component';
import flagLu from '../../../../../../public/images/icons/svg/flag_lu.svg?component';
import flagFr from '../../../../../../public/images/icons/svg/flag_fr.svg?component';
import flagBe from '../../../../../../public/images/icons/svg/flag_be.svg?component';
import flagNl from '../../../../../../public/images/icons/svg/flag_nl.svg?component';
import {getCountryFromUrl} from '../../../../abstracts/utilities.js';

const input = useTemplateRef('input');
const hidden = useTemplateRef('hidden');

const props = defineProps({
  autocomplete: {
    type: [String, Boolean],
    default: 'off'
  },
  id: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'text',
  },
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  rules: {
    type: Array,
    default: () => [],
  },
  cc: {
    type: Array,
    default: () => [],
  },
  forceValidation: {
    type: Boolean,
    default: false,
  },
  inverted: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['update:modelValue', 'validation-change']);

const internalValue = ref(props.modelValue);
const isTouched = ref(false);
const errorMessage = ref('');
const inputId = useId();

watch(() => props.modelValue, (newValue) => {
  internalValue.value = newValue;
});

const selectedOption = computed(() => {
  if(props.cc.filter((item) => props.modelValue.indexOf('+' + item.code) > -1).length) {
    return props.cc.filter((item) => props.modelValue.indexOf('+' + item.code) > -1)[0] || '';
  } else {
    return '';
  }
})

const validate = () => {
  for (const rule of props.rules) {
    const result = rule(internalValue.value);
    if (typeof result === 'string') {
      errorMessage.value = result;
      emit('validation-change', false);
      return false;
    }
  }
  errorMessage.value = '';
  emit('validation-change', true);
  return true;
};

watch([internalValue, () => props.forceValidation, isTouched], () => {
  if (isTouched.value || props.forceValidation) {
    validate();
  } else {
    errorMessage.value = '';
  }
}, { immediate: true });

const handleInput = (event) => {
  internalValue.value = event.target.value;
  emit('update:modelValue', internalValue.value);
};

const handleBlur = () => {
  preventEmptyInput();
  isTouched.value = true;
  validate();
};

const handleClick = () => {
  isTouched.value = true;
  validate();
};

const preventEmptyInput = () => {
  let countryItem = props.cc.filter((item) => item.country === getCountryFromUrl());

  if(!countryItem.length) return;

  console.log(countryItem);
  if(!internalValue.value.length) {
    // empty input
    internalValue.value = '+' + countryItem[0].code;
    input.value.value = '+' + countryItem[0].country;
    hidden.value.value = countryItem[0].country;
    emit('update:modelValue', internalValue.value);
  } else if(selectedOption.value === '' && internalValue.value.indexOf('+') === -1) {
    // missing country code
    let val = internalValue.value;

    internalValue.value = '+' + countryItem[0].code + val;
    input.value.value = '+' + countryItem[0].country + val;
    hidden.value.value = countryItem[0].country;
    emit('update:modelValue', internalValue.value);
  }
}

const hasError = computed(() => (isTouched.value || props.forceValidation) && errorMessage.value !== '');
</script>